import axios, { AxiosRequestConfig, AxiosError } from "axios";
import config from "../config";
import { AuthService } from "./Auth/AuthService";
import AccessCodeServiceInstance from "./AccessCodeService";
import { getSupportedLanguageId } from "../i18n/helper";
import i18n from "../i18n/i18n";
// import {AuthServiceInstance} from './Auth/AuthService';
const axiosInstance = axios.create({
  baseURL: config.ontimeApi,
  // headers: {'Authorization': `bearer ${AuthServiceInstance?.getAccessToken()}`}
});

axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    config.headers = config.headers || {};

    if (AuthService.theOtherInstance().isLoggedIn()) {
      config.headers[
        "Authorization"
      ] = `Bearer ${AuthService.theOtherInstance().getAccessToken()}`;
    }

    // Add the access code header if available
    const accessCode = AccessCodeServiceInstance.getAccessCode();
    if (accessCode) {
      config.headers["X-Access-Code"] = accessCode;
    }

    // Add the Accept-Language header with the user selected language
    config.headers["Accept-Language"] = getSupportedLanguageId(i18n.language);

    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 403 Forbidden responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (error.response?.headers["x-auth-failure-reason"] === "Private event") {
        // Store the current URL for returning after access code entry
        const currentPath = window.location.pathname + window.location.search;

        // Redirect to access code page with return URL
        window.location.href = `/access-code?returnUrl=${encodeURIComponent(
          currentPath
        )}`;
      } else {
        // For other 401 errors, redirect to login or show a message
        AuthService.theOtherInstance().login();
      }
    }
    if (error.response && error.response.status !== 401) {
      try {
        const { appInsights } = require("./AppInsightsService");
        if (appInsights && typeof appInsights.trackException === "function") {
          appInsights.trackException({
            exception: error,
            properties: {
              url: error.config?.url,
              method: error.config?.method,
              status: error.response?.status,
            },
          });
        }
      } catch {
        // Swallow telemetry errors to avoid masking the original Axios error
      }
    }
    return Promise.reject(error);
  }
);

const signalRTimingHubUrl = `${config.ontimeSignalRServer}/timing`;

const apiErrorMessageKeys = [
  "description",
  "errorDescription",
  "detail",
  "title",
  "message",
  "error",
] as const;

const getApiValidationErrors = (
  error: unknown
): Record<string, string[]> | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const responseData = error.response?.data;
  if (!responseData || typeof responseData !== "object") {
    return undefined;
  }

  const errorsValue = (responseData as Record<string, unknown>).errors;
  if (!errorsValue || typeof errorsValue !== "object") {
    return undefined;
  }

  const validationErrors: Record<string, string[]> = {};
  Object.entries(errorsValue as Record<string, unknown>).forEach(
    ([fieldName, fieldErrors]) => {
      if (Array.isArray(fieldErrors)) {
        const messages = fieldErrors
          .filter(
            (fieldError): fieldError is string =>
              typeof fieldError === "string" && fieldError.trim().length > 0
          )
          .map((fieldError) => fieldError.trim());

        if (messages.length > 0) {
          validationErrors[fieldName] = messages;
        }
      } else if (
        typeof fieldErrors === "string" &&
        fieldErrors.trim().length > 0
      ) {
        validationErrors[fieldName] = [fieldErrors.trim()];
      }
    }
  );

  return Object.keys(validationErrors).length > 0
    ? validationErrors
    : undefined;
};

const getApiErrorDescription = (error: unknown): string => {
  const validationErrors = getApiValidationErrors(error);
  if (validationErrors) {
    const validationMessages = Object.values(validationErrors).flat();
    if (validationMessages.length > 0) {
      return validationMessages.join(" ");
    }
  }

  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (typeof responseData === "string" && responseData.trim().length > 0) {
      return responseData.trim();
    }

    if (responseData && typeof responseData === "object") {
      for (const key of apiErrorMessageKeys) {
        const value = (responseData as Record<string, unknown>)[key];
        if (typeof value === "string" && value.trim().length > 0) {
          return value.trim();
        }
      }
    }

    if (typeof error.message === "string" && error.message.trim().length > 0) {
      return error.message.trim();
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message.trim();
  }

  return "Unexpected error";
};

export {
  signalRTimingHubUrl,
  axiosInstance,
  getApiErrorDescription,
  getApiValidationErrors,
};
